import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Star, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function PendingRatingsModal({ pendingRatings, onClose, onComplete }) {
  const [currentRideIndex, setCurrentRideIndex] = useState(0);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const currentRideData = pendingRatings[currentRideIndex];
  const currentUser = currentRideData.users_to_rate[currentUserIndex];
  const totalRides = pendingRatings.length;
  const totalUsersInRide = currentRideData.users_to_rate.length;

  // Calculate total ratings needed
  const totalRatingsNeeded = pendingRatings.reduce((sum, r) => sum + r.users_to_rate.length, 0);
  const completedRatings = pendingRatings.slice(0, currentRideIndex).reduce((sum, r) => sum + r.users_to_rate.length, 0) + currentUserIndex;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`/ratings/${currentRideData.ride.id}`, {
        rated_user_id: currentUser.id,
        stars: rating,
        comment: comment
      });

      // Move to next user or ride
      if (currentUserIndex < totalUsersInRide - 1) {
        // More users to rate in this ride
        setCurrentUserIndex(currentUserIndex + 1);
        setRating(0);
        setComment('');
      } else if (currentRideIndex < totalRides - 1) {
        // Move to next ride
        setCurrentRideIndex(currentRideIndex + 1);
        setCurrentUserIndex(0);
        setRating(0);
        setComment('');
      } else {
        // All done!
        toast.success('All ratings submitted!');
        onComplete();
      }
    } catch (error) {
      if (error.response?.data?.detail?.includes('already rated')) {
        // Skip this one
        if (currentUserIndex < totalUsersInRide - 1) {
          setCurrentUserIndex(currentUserIndex + 1);
        } else if (currentRideIndex < totalRides - 1) {
          setCurrentRideIndex(currentRideIndex + 1);
          setCurrentUserIndex(0);
        } else {
          onComplete();
        }
        setRating(0);
        setComment('');
      } else {
        toast.error('Failed to submit rating');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && toast.error('Please complete your ratings for safety!')}>
      <DialogContent 
        className="sm:max-w-md"
        data-testid="pending-ratings-modal"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-yellow-500" />
            Rate Your Ride Partner
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Progress */}
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Rating {completedRatings + 1} of {totalRatingsNeeded}
            </p>
            <div className="w-full bg-secondary rounded-full h-2 mt-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all"
                style={{ width: `${((completedRatings + 1) / totalRatingsNeeded) * 100}%` }}
              />
            </div>
          </div>

          {/* Ride Info */}
          <div className="bg-accent p-3 rounded-lg">
            <p className="text-sm font-medium">
              {currentRideData.ride.distance}km Ride on {currentRideData.ride.date}
            </p>
            <p className="text-xs text-muted-foreground">{currentRideData.ride.location || currentRideData.ride.postcode}</p>
          </div>

          {/* User Info */}
          <div className="flex items-center gap-3">
            {currentUser.profile_picture ? (
              <img
                src={currentUser.profile_picture}
                alt={currentUser.name}
                className="w-16 h-16 rounded-full object-cover border-2 border-purple-600"
              />
            ) : (
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xl">
                  {currentUser.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
            <div>
              <p className="text-lg font-semibold">{currentUser.name}</p>
              <p className="text-sm text-muted-foreground">{currentUser.experience_level}</p>
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-4 h-4 text-yellow-500" fill="currentColor" />
                <span className="text-sm">{currentUser.avg_rating.toFixed(1)}</span>
                <span className="text-xs text-muted-foreground">({currentUser.total_ratings} ratings)</span>
              </div>
            </div>
          </div>

          {/* Star Rating */}
          <div>
            <p className="font-medium mb-3 text-center">How was your ride with {currentUser.name}?</p>
            <div className="flex gap-2 justify-center" data-testid="star-rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  className="transition-transform hover:scale-110"
                  data-testid={`star-${star}`}
                >
                  <Star
                    className={`w-12 h-12 ${
                      star <= rating
                        ? 'text-yellow-500 fill-yellow-500'
                        : 'text-gray-600'
                    }`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-2">
                {rating === 1 && '😟 Poor experience'}
                {rating === 2 && '😐 Below average'}
                {rating === 3 && '🙂 Good'}
                {rating === 4 && '😊 Great ride!'}
                {rating === 5 && '🌟 Excellent partner!'}
              </p>
            )}
          </div>

          {/* Safety Message */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
            <p className="text-xs text-yellow-600 dark:text-yellow-500 text-center">
              ⚠️ Your honest rating helps keep our community safe
            </p>
          </div>

          {/* Comment */}
          <div>
            <label className="text-sm font-medium block mb-2">
              Comment (optional but encouraged)
            </label>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience..."
              rows={3}
              data-testid="rating-comment"
            />
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full"
            size="lg"
            data-testid="submit-rating"
          >
            {submitting ? 'Submitting...' : completedRatings + 1 < totalRatingsNeeded ? 'Next' : 'Complete'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
